import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    input,
    isDevMode,
    linkedSignal,
    signal,
    viewChildren,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { IChip } from '../../_shared/models/chip.model';
import { ImageData } from '../../_shared/models/image/image-data.model';
import { MediaImage } from '../../_shared/models/media/media-image.model';
import { getRovingIndex } from '../../_shared/utils/roving-index';
import { ChipComponent } from '../../chip/chip.component';
import { MediaCollectionModalComponent } from '../../media/media-collection/media-collection-modal/media-collection-modal.component';
import { ImageItemComponent } from '../../media/media-item/image-item/image-item.component';

export const PRODUCT_GALLERY_MAX_IMAGES = 4;

function capImages(images: MediaImage[]): MediaImage[] {
    const dropped = images.length - PRODUCT_GALLERY_MAX_IMAGES;

    if (dropped > 0 && isDevMode()) {
        console.warn(
            `[ProductGalleryComponent] Renders at most ${PRODUCT_GALLERY_MAX_IMAGES} images; dropped ${dropped}.`
        );
    }

    return images.slice(0, PRODUCT_GALLERY_MAX_IMAGES);
}

@Component({
    selector: 'sirius-product-gallery',
    templateUrl: './product-gallery.component.html',
    styleUrl: './product-gallery.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ChipComponent, ImageItemComponent, MediaCollectionModalComponent, TranslateModule],
    host: {
        'class': 'sir-product-gallery',
        '[class.has-thumbnails]': 'hasThumbnails()',
    },
})
export class ProductGalleryComponent {
    readonly images = input<MediaImage[], MediaImage[]>([], { transform: capImages });
    readonly imageData = input<ImageData | undefined>();
    readonly thumbnailImageData = input<ImageData | undefined>();
    readonly statusLabel = input<IChip | undefined>();

    readonly lightboxOpen = signal(false);

    readonly selectedIndex = linkedSignal<MediaImage[], number>({
        source: this.images,
        computation: (images, previous) => {
            const previousIndex = previous?.value ?? 0;
            return previousIndex < images.length ? previousIndex : 0;
        },
    });

    protected readonly selected = computed<MediaImage | undefined>(() => this.images()[this.selectedIndex()]);
    protected readonly hasThumbnails = computed(() => this.images().length > 1);

    private readonly thumbnailButtons = viewChildren<ElementRef<HTMLButtonElement>>('thumbnailButton');

    protected select(index: number): void {
        this.selectedIndex.set(index);
    }

    protected onKeydown(event: KeyboardEvent, index: number): void {
        const nextIndex = getRovingIndex(event.key, index, this.images().length, 'both');
        if (nextIndex === null) return;

        event.preventDefault();
        this.select(nextIndex);
        this.thumbnailButtons()[nextIndex]?.nativeElement.focus();
    }

    protected openLightbox(): void {
        this.lightboxOpen.set(true);
    }

    protected onLightboxClose(lastViewedIndex: number): void {
        this.lightboxOpen.set(false);
        if (lastViewedIndex >= 0 && lastViewedIndex < this.images().length) {
            this.select(lastViewedIndex);
        }
    }
}
